import { useEffect, useState } from 'react';

import {
  TABLE_ARIA_SORT,
  TABLE_MD_UP_MEDIA_QUERY,
  TABLE_PAGINATION_MIN_PAGE,
  TABLE_SORT_DIRECTION,
} from './table.constants';
import type { TableSortState } from './Table.types';

/**
 * `aria-sort` value for a column header, derived from current `sortState`.
 *
 * @param sortState - Active sort, or `null` if unsorted / no column selected.
 * @param columnId - Header column key; compared to `sortState.columnId`.
 */
export const resolveAriaSort = (
  sortState: TableSortState | null,
  columnId: string,
): 'none' | 'ascending' | 'descending' => {
  if (!sortState || sortState.columnId !== columnId) {
    return TABLE_ARIA_SORT.none;
  }
  if (sortState.direction === TABLE_SORT_DIRECTION.asc) {
    return TABLE_ARIA_SORT.ascending;
  }
  return TABLE_ARIA_SORT.descending;
};

/**
 * Returns a **new array** sorted by `sortState` using comparators from `sortFns`.
 * No-op when `sortState` is null, the column has no comparator, or `sortFn` is missing for that column.
 *
 * @remarks Implemented as a `function` (not `const` + generic arrow) so ESLint/Babel do not misparse `<T>`.
 */
export function sortTableData<T>(
  data: T[],
  sortState: TableSortState | null,
  sortFns: Record<string, (_a: T, _b: T) => number>,
): T[] {
  if (!sortState) {
    return data;
  }
  const sortFn = sortFns[sortState.columnId];
  if (!sortFn) {
    return data;
  }
  const next = data.slice();
  next.sort((a, b) => {
    const cmp = sortFn(a, b);
    return sortState.direction === TABLE_SORT_DIRECTION.asc ? cmp : -cmp;
  });
  return next;
}

/**
 * Whether the viewport matches `TABLE_MD_UP_MEDIA_QUERY` (desktop table vs card layout).
 * SSR-safe: returns `false` until `window` is available, then subscribes to `matchMedia` changes.
 */
export const useIsMdUp = (): boolean => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(TABLE_MD_UP_MEDIA_QUERY).matches;
  });

  const subscribeToMdUpMediaQuery = (): void | (() => void) => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const mq = window.matchMedia(TABLE_MD_UP_MEDIA_QUERY);
    const applyMatchResult = () => {
      setMatches(mq.matches);
    };
    applyMatchResult();
    mq.addEventListener('change', applyMatchResult);
    return () => mq.removeEventListener('change', applyMatchResult);
  };

  useEffect(() => subscribeToMdUpMediaQuery(), []);

  return matches;
};

/**
 * Total page count for local pagination (`Math.ceil`), never less than {@link TABLE_PAGINATION_MIN_PAGE}.
 */
export const totalPagesLocal = (totalItems: number, pageSize: number): number => {
  if (totalItems <= 0) {
    return TABLE_PAGINATION_MIN_PAGE;
  }
  return Math.ceil(totalItems / pageSize);
};

/**
 * Clamps `page` into `[TABLE_PAGINATION_MIN_PAGE, totalPages]` (inclusive).
 * Use before rendering `Table.Pagination` `mode="local"` so `page` stays valid when data shrinks.
 */
export const clampPaginationPage = (page: number, totalPages: number): number => {
  let safePage = page;
  if (safePage < TABLE_PAGINATION_MIN_PAGE) {
    safePage = TABLE_PAGINATION_MIN_PAGE;
  } else if (safePage > totalPages) {
    safePage = totalPages;
  }
  return safePage;
};
